// THE PUBLIC SHELL (Module 09 §4.10) — the chrome a customer sees, which is
// almost none of it.
//
// The office AppShell is wrong here in every particular: it has a nav to
// screens this reader cannot open, a tenant switcher, a search box wired to the
// catalogue, a sign-out button for a session that does not exist, and a locale
// switcher that would let a customer override the locale their own quote was
// written in. The floor shell is wrong for the same kind of reason. So this is
// a third shell — the `/accept-invite` and `/wall` precedent, where the SHELL
// decision belongs to the route and is asserted as a property of a component
// rather than read out of JSX.
//
// WHAT IT DELIBERATELY HAS:
//   * a single centred column that reads on a 360 px phone first — this page
//     is opened from a Telegram message, on a phone, essentially always;
//   * `<html lang>` set from the DOCUMENT's locale (the office sets it from
//     the LocaleSwitcher's effect, which does not render here) — screen
//     readers pick a voice from it and browsers pick hyphenation from it;
//   * the seller's name, and NOTHING before the token resolves. §4.10's
//     not-found page is "generic 404, no tenant leak": no branding, no tenant
//     name, no favicon story that says which factory this was.
//
// WHAT IT DELIBERATELY HAS NOT: any control that mutates anything, any link
// into the authenticated app, and any string that names an internal concept.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Button } from "../../components/button";
import type { Locale } from "../../i18n-dictionaries";
import { htmlLangOf } from "../../lib/locale";
import "./public.css";

// ---------------------------------------------------------------------------
// THE LOCALE BOUNDARY (§4.10) — the public door renders in the DOCUMENT's
// locale WITHOUT changing the language the rest of the tab is in.
// ---------------------------------------------------------------------------
//
// i18next's language is a property of an INSTANCE, and `src/i18n.ts` creates
// exactly one, shared by every screen in the app. So `changeLanguage` on it is
// a cross-session write: a signed-in manager who opens their own customer's
// `/q/{token}` link in the same tab has their OFFICE app switch language, and
// it stays switched when they navigate back (P3-C-1 ledger row 89).
//
// The fix is i18next's own scoping primitive rather than a save/restore dance:
// `cloneInstance` SHARES the resource store by reference and carries its own
// language and its own Translator, so a clone at `ru` cannot be observed from
// the instance it was cloned off. `I18nextProvider` then hands that clone to
// one React subtree, and `useTranslation` inside it resolves through the
// clone — nested providers override their parent for their subtree, which is
// exactly the shape this needs.
//
// WHY THE BOUNDARY IS NOT SIMPLY `PublicShell` ITSELF. The two public screens
// call `useTranslation()` in their OWN body and pass resolved strings DOWN
// into the shell (`<PublicFailure title={t(...)} />`), so a provider rendered
// inside `PublicShell` sits below most of the text a customer reads. The
// boundary therefore has to sit ABOVE the screen components, which is the
// route: `routes/root.tsx`'s `if (isPublicDoorPath(pathname)) return <Outlet/>`
// is the one place in the tree that already knows "this render is a public
// door". Until that line wraps its `<Outlet/>` in this boundary, `PublicShell`
// falls back to driving the shared instance and PUTTING IT BACK on unmount —
// see the effect below, which is the half that can be deleted the day the
// route is wrapped.

/** What `PublicShell` publishes upward once the token resolves. */
interface PublicLocaleChannel {
  readonly setLocale: (locale: Locale) => void;
}

const PublicLocaleContext = createContext<PublicLocaleChannel | undefined>(undefined);

/**
 * Renders its subtree against a locale-scoped i18next instance.
 *
 * It starts on the ambient instance — the token has not resolved, so the
 * document's locale is not known yet, and flashing English over a Russian page
 * is worse than a beat of the browser's own language. `PublicShell` publishes
 * the resolved locale through the context above, and only then does the
 * subtree swap onto the clone.
 */
export function PublicLocaleBoundary({ children }: { children: ReactNode }) {
  // Outside its OWN provider, so this is the ambient (office) instance — the
  // one being protected, and the one the clone shares its store with.
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<Locale | undefined>(undefined);
  const scoped = useMemo(
    () => (locale === undefined ? i18n : i18n.cloneInstance({ lng: locale })),
    [i18n, locale],
  );
  // `setLocale` from useState is stable, so the channel never changes identity
  // and the publishing effect below fires once per resolved locale.
  const channel = useMemo<PublicLocaleChannel>(() => ({ setLocale }), []);
  return (
    <PublicLocaleContext.Provider value={channel}>
      <I18nextProvider i18n={scoped}>{children}</I18nextProvider>
    </PublicLocaleContext.Provider>
  );
}

export interface PublicShellProps {
  /** The locale the DOCUMENT is written in, already narrowed to one we
   * actually translated (lib/public-view.publicRenderLocale). `undefined`
   * while the token has not resolved — the shell then leaves the language
   * alone rather than flashing English over a Russian page. */
  locale?: Locale | undefined;
  /** The seller's display name — rendered ONLY once a token resolved. */
  sellerName?: string | undefined;
  children: ReactNode;
  "data-testid"?: string;
}

export function PublicShell({
  locale,
  sellerName,
  children,
  "data-testid": testId,
}: PublicShellProps) {
  const { t } = useTranslation();
  const boundary = useContext(PublicLocaleContext);

  // THE LOCALE RULE, applied (§4.10) — published UPWARD to the boundary, which
  // swaps its subtree onto a scoped instance. Nothing shared is touched on
  // this path, so there is nothing to undo when the customer closes the page.
  useEffect(() => {
    if (locale === undefined || boundary === undefined) return;
    boundary.setLocale(locale);
  }, [locale, boundary]);

  // THE BRIDGE IS GONE (2026-09-10). It drove the SHARED instance and restored
  // it on unmount, for a tree with no boundary above it. `routes/root.tsx`'s
  // public-door branch now wraps the outlet in `PublicLocaleBoundary`, which is
  // above BOTH screens, so there is no such tree any more and nothing shared is
  // ever touched. A mutation-and-restore closes the leak only while every
  // unmount runs; scoping cannot leak at all. If a public screen is ever mounted
  // outside that branch, the fix is to wrap ITS route — not to bring the bridge
  // back, which would put the office's language one missed cleanup away again.

  // `<html lang>` is the document's own attribute, not i18next state: screen
  // readers pick a voice from it and browsers pick hyphenation from it, so it
  // has to say what is actually on the glass. It needs no restore — the
  // office's LocaleSwitcher sets it from an effect that runs on every mount
  // (components/locale-switcher.tsx), so navigating back re-states it.
  useEffect(() => {
    if (locale === undefined) return;
    // The registry's BCP 47 tag, not the raw code: Simplified Chinese is
    // written `zh-Hans`, which is what screen readers and font selection
    // read (rev-7; the office's switcher does the same through htmlLangOf).
    document.documentElement.lang = htmlLangOf(locale);
  }, [locale]);

  return (
    <div className="app app-public" data-testid={testId ?? "public-shell"}>
      <main className="app-main public-main">
        <div className="public-column">
          {sellerName !== undefined ? (
            <p className="public-seller" data-testid="public-seller">
              {t("public.quote.from", { seller: sellerName })}
            </p>
          ) : null}
          {children}
        </div>
        <p className="public-footer">{t("public.shell.poweredBy")}</p>
      </main>
    </div>
  );
}

/**
 * The failure card both doors share.
 *
 * It is NOT `ErrorState` from components/states.tsx, and the difference is the
 * audience: that card says "Try again" beside a request id to somebody who can
 * paste it into a ticket. This one has to be readable by a customer who has no
 * idea what a request id is, so the id is labelled "Reference" and the sentence
 * above it says the thing that actually matters — that their offer or order is
 * unaffected by our failure.
 */
export function PublicFailure({
  title,
  body,
  requestId,
  onRetry,
  "data-testid": testId,
}: {
  title: string;
  body: string;
  requestId?: string | undefined;
  onRetry?: (() => void) | undefined;
  "data-testid"?: string;
}) {
  const { t } = useTranslation();
  return (
    <section className="public-card public-card--quiet" data-testid={testId} role="alert">
      <h1 className="public-title">{title}</h1>
      <p className="public-body">{body}</p>
      {onRetry !== undefined ? (
        <Button variant="primary" className="public-action" onClick={onRetry}>
          {t("public.shell.retry")}
        </Button>
      ) : null}
      {requestId !== undefined ? (
        <p className="public-meta">
          {t("public.shell.requestId")}: <code>{requestId}</code>
        </p>
      ) : null}
    </section>
  );
}
